<template>
  <BaseSidebar
    :model-value="modelValue"
    background-color="#FFFFFF"
    width="550px"
    @update:model-value="(value: boolean) => emit('update:modelValue', value)"
  >
    <template #header="{ close }">
      <div class="flex flex-col items-start p-6 pb-4" data-testid="post-station-detail-sidebar">
        <div class="flex items-start justify-between w-full">
          <div class="relative">
            <button
              type="button"
              class="flex items-center gap-1 text-xl font-bold text-textPrimary"
              data-testid="post-station-detail-domain-select"
              @click="toggleDomainMenu"
            >
              {{ selectedDomain?.label }}
              <svg class="w-5 h-5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path :d="mdiChevronDown" fill="currentColor" />
              </svg>
            </button>

            <template v-if="isDomainMenuOpen">
              <div
                class="fixed inset-0 z-40"
                data-testid="post-station-detail-domain-backdrop"
                @click="closeDomainMenu"
              />
              <div
                class="absolute z-50 mt-2 flex flex-col overflow-hidden bg-white border border-gray-200 rounded-lg shadow-md py-2 min-w-[240px]"
              >
                <button
                  v-for="domain in domains"
                  :key="domain.key"
                  type="button"
                  class="flex w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                  :class="
                    domain.key === selectedKey ? 'font-bold text-primary' : 'text-textPrimary'
                  "
                  :data-testid="`post-station-detail-domain-${domain.key}-item`"
                  @click="selectDomain(domain.key)"
                >
                  {{ domain.label }}
                </button>
              </div>
            </template>
          </div>

          <button
            type="button"
            class="text-textPrimary hover:text-textPrimary/50 transition mt-1"
            data-testid="post-station-detail-close-button"
            @click="close"
          >
            <svg class="w-6 h-6" viewBox="0 0 24 24" aria-hidden="true">
              <path :d="mdiClose" fill="currentColor" />
            </svg>
          </button>
        </div>

        <p
          class="mt-1 text-left text-[14px] font-normal leading-[18px] tracking-[0.07px] text-[#68688D]"
        >
          Detalhes do domínio
        </p>
        <hr class="w-full border-t border-[#2F7F91]/25 mt-4" />
      </div>
    </template>

    <PostStationDetailSkeleton v-if="isLoading" />

    <div
      v-else-if="hasError"
      class="flex flex-col items-center justify-center gap-4 py-16 text-center"
      data-testid="post-station-detail-error-state"
    >
      <svg class="w-10 h-10 text-negative" viewBox="0 0 24 24" aria-hidden="true">
        <path :d="mdiAlertCircleOutline" fill="currentColor" />
      </svg>
      <p class="font-bold text-textPrimary">Não foi possível carregar os detalhes do domínio</p>
      <p
        v-if="errorMessage"
        class="text-sm text-neutral50"
        data-testid="post-station-detail-error-message"
      >
        {{ errorMessage }}
      </p>
      <OutlinedButton
        text="Tentar novamente"
        width="200px"
        data-testid="post-station-detail-retry-button"
        @click="retryFetch"
      />
    </div>

    <div v-else-if="row && drilldown" class="flex flex-col gap-4 pb-4">
      <!-- Campos nulos do drill-down caem no valor da row ("Não informado" no card quando vazio). -->
      <CardDutyWithDoctor
        :formation="drilldown.formation ?? row.detail.formation"
        :team="drilldown.team ?? row.detail.team"
        :position="drilldown.position ?? row.detail.position"
        :shift="drilldown.shift ?? row.detail.shift"
        :duty-label="row.detail.dutyLabel"
        :doctor-name="drilldown.doctor.name"
        :doctor-crm="drilldown.doctor.crm"
        :avatar-url="drilldown.doctor.avatarUrl"
        :active="false"
        :show-requirements="true"
        data-testid="post-station-detail-doctor-card"
      />

      <LabelBadge :label="statusBadge.label" :color="statusBadge.color" :icon="statusBadge.icon" />

      <div class="flex flex-col divide-y divide-gray-200">
        <div
          class="flex items-center justify-between py-3"
          data-testid="post-station-detail-company-row"
        >
          <span class="font-semibold text-sm text-textPrimary">Empresa</span>
          <span class="text-sm text-textPrimary text-right">{{ drilldown.company }}</span>
        </div>
        <div
          class="flex items-center justify-between py-3"
          data-testid="post-station-detail-unit-row"
        >
          <span class="font-semibold text-sm text-textPrimary">Unidade</span>
          <span class="text-sm text-textPrimary text-right">{{ drilldown.unit }}</span>
        </div>
        <div
          class="flex items-center justify-between py-3"
          data-testid="post-station-detail-schedule-row"
        >
          <span class="font-semibold text-sm text-textPrimary">Escala</span>
          <span class="text-sm text-textPrimary text-right">{{ drilldown.schedule }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-1 text-left">
        <p class="text-xs font-semibold uppercase tracking-wide text-neutral50">
          Critérios avaliados
        </p>

        <div v-if="drilldown.criteria.length" class="flex flex-col">
          <div
            v-for="(criterion, index) in drilldown.criteria"
            :key="criterion.key"
            class="flex items-center justify-between gap-4 py-3"
            :data-testid="`post-station-detail-criterion-${index}`"
          >
            <div class="flex flex-col text-left">
              <span class="text-sm text-textPrimary">{{ criterion.label }}</span>
              <span v-if="criterion.description" class="text-xs text-neutral50">
                {{ criterion.description }}
              </span>
            </div>
            <LabelBadge
              v-if="selectedKey === 'exception' && criterion.actual"
              :label="exceptionBadgeFor(criterion.actual).label"
              :color="exceptionBadgeFor(criterion.actual).color"
              size="small"
              :icon="exceptionBadgeFor(criterion.actual).icon"
            />
            <!-- Q1 — fora do time as pernas podem vir com `result=false`: não é reprovação,
                 o registro só não é exigido. Neutro, nunca o X vermelho. -->
            <LabelBadge
              v-else-if="registrationNotRequired && !criterion.passed"
              :label="COMPLIANCE_STATE_TITLES.not_required"
              color="gray"
              size="small"
              :icon="mdiMinusCircleOutline"
              :data-testid="`post-station-detail-criterion-${index}-not-required`"
            />
            <LabelBadge
              v-else
              :label="criterion.passed ? 'Sim' : 'Não'"
              :color="criterion.passed ? 'green' : 'red'"
              size="small"
              :icon="criterion.passed ? mdiCheckCircle : mdiCloseCircle"
            />
          </div>
        </div>

        <p v-else class="text-sm text-neutral50 py-3">Nenhum critério avaliado neste domínio.</p>
      </div>

      <!-- F4 (M5, balão ④) — o domínio de registro diz, em texto, se a política
           da escala BLOQUEIA a aprovação ou é só informativa. Não existe botão
           "Aprovar plantão": quem aprova é o Audit Bot, e um botão desabilitado
           prometeria uma ação que o produto não tem. -->
      <template v-if="isRegistrationDomain">
        <div
          v-if="registrationBlocked"
          role="note"
          data-testid="post-station-registration-blocked"
          class="flex flex-col gap-1 rounded-md border border-alert bg-alert/10 px-4 py-3 text-left text-sm text-textPrimary"
        >
          <span class="font-bold">{{ REGISTRATION_BLOCKED_TITLE }}</span>
          <span>{{ registrationBlockedMessage }}</span>
        </div>

        <!-- F4-FX1 (revisto): rótulo, não veredito, e só quando o domínio NÃO
             reprovou — a tela não afirma bloqueio nem não-bloqueio por conta
             própria. Reprovou? Quem fala é o banner acima. -->
        <div
          v-else-if="registrationNotEvaluated"
          role="note"
          data-testid="post-station-registration-not-evaluated"
          class="flex flex-col gap-1 rounded-md bg-primary5 px-4 py-3 text-left text-sm text-textPrimary"
        >
          <span class="font-bold">{{ REGISTRATION_NOT_EVALUATED }}</span>
          <span>{{ REGISTRATION_NOT_EVALUATED_TEXT }}</span>
        </div>

        <div
          v-else-if="drilldown.registrationBlocking === false"
          role="note"
          data-testid="post-station-registration-informative"
          class="flex flex-col gap-1 rounded-md bg-primary5 px-4 py-3 text-left text-sm text-textPrimary"
        >
          <span class="font-bold">{{ REGISTRATION_INFORMATIVE_TITLE }}</span>
          <span>{{ REGISTRATION_INFORMATIVE_TEXT }}</span>
        </div>

        <div class="flex flex-wrap gap-2">
          <button
            type="button"
            class="rounded-md border border-primary30 px-3 py-2 text-sm text-primary30"
            data-testid="post-station-registration-view-record-button"
            @click="openOriginModule"
          >
            Ver registro
          </button>
          <button
            v-if="canEmitActivityRecord && !registrationNotRequired"
            type="button"
            class="rounded-md border border-primary30 px-3 py-2 text-sm text-primary30"
            data-testid="post-station-registration-send-record-button"
            @click="openStationHub"
          >
            Enviar registro
          </button>
        </div>

        <PostStationFrozenRecordsTable
          :records="frozenRecords"
          :timezone="registrationTimezone"
          @view-record="openOriginModule"
        />
      </template>
    </div>

    <template #footer>
      <FilledButton
        :text="ctaLabel"
        width="100%"
        class="!bg-primary30"
        :disabled="!canOpenOrigin"
        data-testid="open-origin-module-button"
        @click="openOriginModule"
      />
    </template>
  </BaseSidebar>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import {
  mdiAlertCircleOutline,
  mdiCheckCircle,
  mdiChevronDown,
  mdiClockOutline,
  mdiClose,
  mdiCloseCircle,
  mdiMinusCircleOutline
} from '@mdi/js'

import BaseSidebar from '@/components/designSystem/Sidebar/BaseSidebar.vue'
import CardDutyWithDoctor from '@/components/designSystem/Cards/CardDutyWithDoctor.vue'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import OutlinedButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import PostStationDetailSkeleton from '@/components/designSystem/Skeleton/PostStationDetailSkeleton.vue'
import Network from '@/Network'
import type { StationRegistrationActivityRecord } from '@/Network/Services/StationService'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import { getApiErrorMessage } from '@/utils/apiError'

import PostStationFrozenRecordsTable from './PostStationFrozenRecordsTable.vue'
import {
  COMPLIANCE_STATE_TITLES,
  DOMAIN_CTA_LABELS,
  DOMAIN_KEY_TO_API,
  exceptionBadgeFor,
  REGISTRATION_BLOCKED_TITLE,
  REGISTRATION_INFORMATIVE_TEXT,
  REGISTRATION_INFORMATIVE_TITLE,
  REGISTRATION_LEG_LABELS,
  REGISTRATION_NOT_EVALUATED,
  REGISTRATION_NOT_EVALUATED_TEXT,
  REGISTRATION_NOT_REQUIRED_TITLE,
  registrationBlockedText
} from '../constants'
import { mapDomainDetail } from '../mappers'
import { resolveOpenStationRoute, resolveOriginRoute } from '../originRoutes'
import type { PostStationCompliance, PostStationDomainDrilldown, PostStationRow } from '../types'

type DomainKey = keyof PostStationCompliance

const props = defineProps<{
  modelValue: boolean
  row: PostStationRow | null
  // Null → sem fetch do drill-down.
  evaluationId: number | null
  // Domínio exibido ao abrir; ausente/null → primeiro domínio da linha.
  initialDomainKey?: DomainKey | null
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const router = useRouter()
const permissionsStore = usePermissionsStore()

// Declarado inline (não importado do LabelBadge.vue) para não quebrar o build
// webpack — o shim de .vue só expõe o export default.
type DomainBadge = { label: string; color: 'green' | 'red' | 'gray'; icon: string }

const domains = computed(() => props.row?.detail.domains ?? [])
const selectedKey = ref<DomainKey | null>(null)
const isDomainMenuOpen = ref(false)

const selectedDomain = computed(
  () => domains.value.find((domain) => domain.key === selectedKey.value) ?? domains.value[0] ?? null
)

// Cache por domínio durante a sessão da sidebar (limpo a cada abertura):
// trocar de domínio e voltar não refaz a request.
const isLoading = ref(false)
const hasError = ref(false)
// Mensagem específica do service (permissão, rede...) exibida sob o título fixo.
const errorMessage = ref<string | null>(null)
const detailCache = ref<Partial<Record<DomainKey, PostStationDomainDrilldown>>>({})

const drilldown = computed(() =>
  selectedKey.value ? (detailCache.value[selectedKey.value] ?? null) : null
)

// Guard de corrida: só a request mais recente escreve loading/erro; respostas
// antigas ainda são cacheadas (o dado é por domínio).
let fetchSeq = 0

const fetchDomainDetail = async (key: DomainKey) => {
  if (props.evaluationId === null) return
  // Os registros tardios (Q28) são do PLANTÃO, não da avaliação: carregam em
  // paralelo, só no domínio de registro e sem atrasar o drill-down.
  const seq = ++fetchSeq
  if (key === 'registration' && props.row) void loadFrozenRecords(props.row, seq)
  isLoading.value = true
  hasError.value = false
  errorMessage.value = null
  try {
    const response = await Network.postStation.getDomainDetail(
      props.evaluationId,
      DOMAIN_KEY_TO_API[key]
    )
    detailCache.value[key] = mapDomainDetail(response)
    if (seq === fetchSeq) {
      isLoading.value = false
    }
  } catch (error) {
    if (seq === fetchSeq) {
      hasError.value = true
      errorMessage.value = getApiErrorMessage(
        error,
        'Não foi possível carregar os detalhes do domínio da avaliação'
      )
      isLoading.value = false
    }
  }
}

const toggleDomainMenu = () => {
  isDomainMenuOpen.value = !isDomainMenuOpen.value
}

const closeDomainMenu = () => {
  isDomainMenuOpen.value = false
}

const selectDomain = (key: DomainKey) => {
  selectedKey.value = key
  closeDomainMenu()
  if (detailCache.value[key]) {
    // Cache hit: invalida requests em voo (não podem mais mexer em loading/erro).
    fetchSeq += 1
    isLoading.value = false
    hasError.value = false
    return
  }
  fetchDomainDetail(key)
}

const retryFetch = () => {
  if (selectedKey.value) fetchDomainDetail(selectedKey.value)
}

// Cada abertura é uma nova sessão: zera cache/estados e busca o domínio inicial.
watch(
  () => props.modelValue,
  (isOpen) => {
    isDomainMenuOpen.value = false
    if (!isOpen) return

    detailCache.value = {}
    frozenRecords.value = []
    hasError.value = false
    fetchSeq += 1

    const initialKey = props.initialDomainKey ?? domains.value[0]?.key ?? null
    selectedKey.value = initialKey
    if (initialKey) {
      fetchDomainDetail(initialKey)
    } else {
      isLoading.value = false
    }
  }
)

const statusBadge = computed<DomainBadge>(() => {
  const state = drilldown.value?.state

  // Q1 — fora do time: selo neutro, nem aprovado nem reprovado.
  if (state === 'not_required') {
    return { label: REGISTRATION_NOT_REQUIRED_TITLE, color: 'gray', icon: mdiMinusCircleOutline }
  }

  if (selectedKey.value === 'exception' && drilldown.value?.criteria[0]?.actual) {
    const actual = drilldown.value.criteria[0].actual
    if (state === 'fail') {
      return { label: 'Reprovado – Excepcionalidade pendente', color: 'red', icon: mdiCloseCircle }
    }
    if (actual === 'Aprovado') {
      return {
        label: 'Aprovado – Excepcionalidade aprovada',
        color: 'green',
        icon: mdiCheckCircle
      }
    }
    if (actual === 'Sem pedido') {
      return {
        label: 'Aprovado – Sem pedido de excepcionalidade',
        color: 'green',
        icon: mdiCheckCircle
      }
    }
    // Recusado, Revertido, Cancelado → domínio OK mas excepcionalidade reprovada
    return {
      label: 'Aprovado – Excepcionalidade reprovada',
      color: 'green',
      icon: mdiCheckCircle
    }
  }

  if (state === 'fail') {
    return { label: 'Critérios reprovados', color: 'red', icon: mdiCloseCircle }
  }
  if (state === 'pending') {
    return { label: 'Avaliação pendente', color: 'gray', icon: mdiClockOutline }
  }
  return { label: 'Todos os critérios aprovados', color: 'green', icon: mdiCheckCircle }
})

const ctaLabel = computed(() =>
  selectedKey.value ? DOMAIN_CTA_LABELS[selectedKey.value] : 'Abrir módulo de origem'
)

// A origem vem da resposta do drill-down; a navegação desmonta o painel — não
// precisa fechar a sidebar antes.
const canOpenOrigin = computed(
  () => Boolean(drilldown.value) && selectedKey.value !== null && props.row !== null
)

const openOriginModule = () => {
  if (!drilldown.value || !selectedKey.value || !props.row) return
  router.push(resolveOriginRoute(selectedKey.value, drilldown.value.origin, props.row))
}

/* ----------------------------------------------------------------------- *
 * F4 — domínio `registration`: gate, CTAs e registros recebidos após o
 * fechamento (M5, balões ③, ④, ⑥ e ⑦).
 * ----------------------------------------------------------------------- */

const isRegistrationDomain = computed(() => selectedKey.value === 'registration')

/**
 * "Enviar registro" é gateado pela permissão de EMISSÃO do `ds-attendance`,
 * resolvida pelo grafo (empresa → unidade → escala) — por isso
 * `checkAnyRessources` e não `check`, igual ao Detalhes do Plantão.
 */
const canEmitActivityRecord = computed(() =>
  permissionsStore.checkAnyRessources({
    permission: AuthorizationPermissions.AttendanceEmitting,
    resourceType: AuthorizationResourcesAndSubjects.DsAttendance
  })
)

// O hub "Enviar registro" (M1/M2) mora no Detalhes do Plantão; a sidebar leva
// até lá em vez de duplicar o wizard de 5 passos dentro do painel.
const openStationHub = () => {
  if (!props.row) return
  router.push(resolveOpenStationRoute(props.row))
}

/** Q1 — fora do time: sem bloqueio, sem "não avaliado" e sem CTA de envio. */
const registrationNotRequired = computed(
  () => isRegistrationDomain.value && drilldown.value?.state === 'not_required'
)

/**
 * Banner de bloqueio: só quando o domínio REPROVOU. `registrationBlocking`
 * ausente = backend anterior à F4, e lá o registro bloqueia — afrouxar por
 * omissão anunciaria "informativo" um domínio que na prática segura o plantão.
 *
 * F4-FX1 (revisto) — a ausência de política vigente NÃO suprime mais o banner.
 * A correção do backend removeu apenas o rebaixamento para `PENDING`;
 * `resolveRegistrationBlocking` continua devolvendo `true` nesse caminho e
 * `NONE` continua mapeado para `FAIL`, então o plantão realmente para em
 * "Revisar". Quem afirma bloqueio (ou não-bloqueio) é sempre o backend; a tela
 * só rotula.
 */
const registrationBlocked = computed(
  () =>
    !registrationNotRequired.value &&
    isRegistrationDomain.value &&
    drilldown.value?.state === 'fail' &&
    drilldown.value?.registrationBlocking !== false
)

/**
 * Rótulo "Não avaliado" (balão ⑤ do M5): rotulagem, não veredito — e só onde é
 * verdade. Com o domínio REPROVADO quem fala é o banner acima (que já carrega a
 * frase "sem política de registro vigente"); anunciar "não avaliado" ali fazia a
 * sidebar contradizer o próprio grid, que pinta "Revisar" na mesma linha.
 */
const registrationNotEvaluated = computed(
  () =>
    !registrationNotRequired.value &&
    isRegistrationDomain.value &&
    props.row?.registrationPolicyInEffect === false &&
    drilldown.value?.state !== 'fail'
)

// A perna que falta é o único dado acionável do banner: vem dos critérios
// reprovados de check-in/check-out, e não de uma segunda leitura do registro.
const missingLegs = computed(() =>
  (drilldown.value?.criteria ?? [])
    .filter((criterion) => !criterion.passed && criterion.key in REGISTRATION_LEG_LABELS)
    .map((criterion) => REGISTRATION_LEG_LABELS[criterion.key])
)

const registrationBlockedMessage = computed(() =>
  registrationBlockedText(missingLegs.value, props.row?.registrationPolicyInEffect !== false)
)

/**
 * Q28 — os registros que chegaram DEPOIS do fechamento. Vêm do payload cru de
 * `GET /stations/:doctorStationId/registration` (o recorte achatado do
 * normalizador não os carrega).
 *
 * A leitura é acessória: se a rota não existir ou falhar, a sidebar segue
 * inteira e a tabela some — o estado de erro da sidebar é do drill-down, que é
 * o que o usuário veio ver.
 */
const frozenRecords = ref<StationRegistrationActivityRecord[]>([])
const registrationTimezone = ref<string | null>(null)

const loadFrozenRecords = async (row: PostStationRow, seq: number) => {
  frozenRecords.value = []
  registrationTimezone.value = null
  try {
    const registration = await Network.station?.getStationRegistration?.(row.doctorStationId)
    // I10 — a chamada é fire-and-forget e o plantão muda: sem o mesmo guard do
    // drill-down, fechar a sidebar de A e abrir a de B listava os registros
    // tardios DE A na tabela de B, com "Ver registro" apontando para outro
    // plantão.
    if (seq !== fetchSeq) return
    const raw = registration?.raw
    if (!raw) return
    registrationTimezone.value = raw.timezone ?? null
    frozenRecords.value = (raw.activityRecords ?? []).filter((record) => record.afterClosure)
  } catch (error) {
    if (seq !== fetchSeq) return
    console.error('Não foi possível carregar os registros do plantão', error)
  }
}
</script>
